import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Users, Luggage, Shield, Star } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const FleetSection = () => {
  const { translate } = useLanguage();

  const vehicles = [
    {
      category: translate('prestige'),
      title: translate('mercedesS'),
      image: "https://customer-assets.emergentagent.com/job_swisslux-service/artifacts/h5z3l7c2_IMG_6713.WEBP",
      description: translate('mercedesSDescription'),
      passengers: `1-3 ${translate('passengers')}`,
      luggage: `3 ${translate('luggage')}`,
      features: ["Cuir premium", "Climatisation", "Wi-Fi", "Minibar"],
      price: `${translate('from')} CHF 120/h`
    },
    {
      category: translate('comfort'),
      title: translate('mercedesE'),
      image: "https://customer-assets.emergentagent.com/job_limo-service-1/artifacts/6vtkm6d1_IMG_6716.WEBP",
      description: translate('mercedesEDescription'),
      passengers: `1-3 ${translate('passengers')}`,
      luggage: `3 ${translate('luggage')}`,
      features: ["Intérieur premium", "Navigation GPS", "Système audio", "Confort optimal"],
      price: `${translate('from')} CHF 90/h`
    },
    {
      category: translate('group'),
      title: translate('mercedesV'),
      image: "https://customer-assets.emergentagent.com/job_limo-service-1/artifacts/g1luyze6_IMG_6715.WEBP",
      description: translate('mercedesVDescription'),
      passengers: `${translate('upTo')} 7 ${translate('passengers')}`,
      luggage: `7 ${translate('luggage')}`,
      features: ["Sièges individuels", "Espace généreux", "Climatisation", "Confort famille"],
      price: `${translate('from')} CHF 110/h`
    },
    {
      category: translate('economy'),
      title: translate('standardTaxi'),
      image: "https://customer-assets.emergentagent.com/job_limo-service-1/artifacts/zu6ufa8v_IMG_6724.PNG",
      description: translate('standardTaxiDescription'),
      passengers: `1-4 ${translate('passengers')}`,
      luggage: `2 ${translate('luggage')}`,
      features: ["Service rapide", "Tarif compétitif", "Ponctualité", "Confort standard"],
      price: `${translate('from')} CHF 60/h`
    },
    {
      category: translate('events'),
      title: translate('sprinter'),
      image: "https://customer-assets.emergentagent.com/job_limo-service-1/artifacts/i6929vbu_IMG_6717.PNG",
      description: translate('sprinterDescription'),
      passengers: `${translate('upTo')} 16 ${translate('passengers')}`,
      luggage: `16+ ${translate('luggage')}`,
      features: ["Grand espace", "Équipement moderne", "Sécurité renforcée", "Excursions"],
      price: `${translate('from')} CHF 150/h`
    },
    {
      category: translate('largeGroups'),
      title: translate('luxuryCoach'),
      image: "https://customer-assets.emergentagent.com/job_swisslux-service/artifacts/4efpc0tu_IMG_6718%202.AVIF",
      description: translate('luxuryCoachDescription'),
      passengers: `${translate('upTo')} 50 ${translate('passengers')}`,
      luggage: "Soutes spacieuses",
      features: ["Confort premium", "Toilettes", "Wi-Fi", "Système de divertissement"],
      price: translate('customQuote')
    }
  ];

  const getCategoryColor = (category) => {
    if (category.includes("PRESTIG") || category.includes("PRESTÍŽ") || category.includes("ПРЕСТИЖ")) return "bg-yellow-500 text-black";
    if (category.includes("CONFORT") || category.includes("COMFORT") || category.includes("KOMFOR") || category.includes("КОМФОР")) return "bg-blue-500 text-white";
    if (category.includes("GROUP") || category.includes("GRUPPE") || category.includes("GRUPPO") || category.includes("ГРУПА")) return "bg-green-500 text-white";
    if (category.includes("ÉCONOM") || category.includes("ECONOM") || category.includes("WIRTSCHAFT") || category.includes("ЕКОНОМ")) return "bg-gray-500 text-white";
    if (category.includes("EVENT") || category.includes("DOGAĐAJ")) return "bg-purple-500 text-white";
    if (category.includes("GRAND") || category.includes("LARGE") || category.includes("GROSSE") || category.includes("ВЕЛИК")) return "bg-red-500 text-white";
    return "bg-gray-500 text-white";
  };

  return (
    <section id="fleet" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {translate('fleetTitle')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {translate('fleetDescription')}
          </p>
        </div>

        {/* Fleet Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {vehicles.map((vehicle, index) => (
            <Card key={index} className="group hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden">
              <div className="aspect-video relative overflow-hidden">
                <img 
                  src={vehicle.image} 
                  alt={vehicle.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4">
                  <Badge className={`${getCategoryColor(vehicle.category)} font-semibold`}>
                    {vehicle.category}
                  </Badge>
                </div>
                <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                  {vehicle.price}
                </div>
              </div>
              
              <CardContent className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{vehicle.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{vehicle.description}</p>
                
                {/* Vehicle specs */}
                <div className="flex items-center justify-between mb-4 text-sm text-gray-500">
                  <div className="flex items-center space-x-1">
                    <Users className="w-4 h-4" />
                    <span>{vehicle.passengers}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Luggage className="w-4 h-4" />
                    <span>{vehicle.luggage}</span>
                  </div>
                </div>
                
                {/* Features */}
                <div className="space-y-2">
                  {vehicle.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2 text-sm text-gray-600">
                      <Star className="w-3 h-3 text-yellow-500" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Special Services */}
        <div className="mt-16 bg-gray-50 rounded-lg p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            {translate('specialServices')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="flex items-start space-x-4">
              <div className="bg-yellow-500 text-black w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{translate('schoolTransport')}</h4>
                <p className="text-gray-600">
                  {translate('schoolTransportDescription')}
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="bg-yellow-500 text-black w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{translate('pmrVehicles')}</h4>
                <p className="text-gray-600">
                  {translate('pmrVehiclesDescription')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FleetSection;